/**

 * DroneOrbitalDropWeaponOverlay

 *

 * When Drone 79 (Orbital Drop Command) equips a weapon on the first unit

 * deployed each turn, animates weapons_box.png from the drone slot to the

 * target unit slot.

 *

 * Action-log entries with type `drone-orbital-drop-weapon-equipped` drive

 * live animations (server pushes via orders/drone79WeaponEquipFx.js).

 *

 * Tutorial mission 3 (chapter_1_mission_3): replays the animation once at

 * battle start because the bot already equipped Proton Blaster on Sankrat

 * Drill Sergeant before the player's first turn.

 */



import React, { useEffect, useRef, useState, useCallback } from 'react';

import { createPortal } from 'react-dom';

import { getBattlebotSlotCenterPixels } from './components/BattlebotPortalOverlay';

import { playSoundEffect } from './hooks/useSoundEffects';

import {

  buildMission3IntroOrbitalDropEntry,

  shouldScheduleMission3OrbitalDropIntro,

} from './effects/drone79OrbitalDropMission3';

import './DroneOrbitalDropWeaponOverlay.css';



const WEAPONS_BOX_IMAGE_SRC = '/images/weapons_box.png';

const BOX_TRAVEL_DURATION_MS = 650;

const BOX_FADE_DURATION_MS = 250;

const TOTAL_ANIMATION_MS = BOX_TRAVEL_DURATION_MS + BOX_FADE_DURATION_MS + 150;



const SEEN_KEY_HARD_CAP = 250;

const SEEN_KEY_SOFT_CAP = 100;



function actionLogEntryKey(entry) {

  if (!entry) return 'no-entry';

  return `${entry.sequence ?? 'no-seq'}:${entry.timestamp ?? 'no-ts'}:${entry.weaponInstanceId ?? entry.targetUnitInstanceId ?? 'no-instance'}`;

}



function clientDroneZoneFor(entry, userId) {

  if (!entry || entry.ownerPlayerId == null) return null;

  const isLocalOwner = String(entry.ownerPlayerId) === String(userId);

  return isLocalOwner ? 'your_drone' : 'opponent_drone';

}



function clientUnitSlotZoneFor(entry, userId) {

  if (!entry || !entry.targetZone) return null;

  const isLocalOwner = String(entry.ownerPlayerId) === String(userId);

  const prefix = isLocalOwner ? 'your_' : 'opponent_';

  return `${prefix}${entry.targetZone}`;

}



function findDroneSlotElement(entry, userId) {

  if (typeof document === 'undefined' || !document.querySelector) return null;

  const droneZone = clientDroneZoneFor(entry, userId);

  if (!droneZone) return null;

  if (entry.droneInstanceId) {

    const byInstance = document.querySelector(

      `[data-drone-zone="${droneZone}"][data-instance-id="${entry.droneInstanceId}"]`

    );

    if (byInstance) return byInstance;

  }

  if (entry.droneIndex == null) return null;

  return document.querySelector(

    `[data-drone-zone="${droneZone}"][data-drone-index="${entry.droneIndex}"]`

  );

}



function getVisibleElementCenter(el) {

  if (!el) return null;

  const card =

    el.querySelector?.('.order-design-card-bounds') ||

    el.querySelector?.('.order-design-minimal');

  if (card) {

    const cr = card.getBoundingClientRect();

    if (cr.width > 0 && cr.height > 0) {

      return { x: cr.left + cr.width / 2, y: cr.top + cr.height / 2 };

    }

  }

  const r = el.getBoundingClientRect();

  if (r.width <= 0 || r.height <= 0) return null;

  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };

}



function getUnitTargetCenterPixels(entry, userId) {

  if (typeof document === 'undefined') return null;



  if (entry.targetUnitInstanceId) {

    const suffix = `_${String(entry.targetUnitInstanceId)}`;

    let bestCenter = null;

    let bestArea = -1;

    document.querySelectorAll('[data-unit-key]').forEach((node) => {

      const key = node.getAttribute('data-unit-key') || '';

      if (!key.endsWith(suffix)) return;

      const style = window.getComputedStyle(node);

      if (style.display === 'none' || style.visibility === 'hidden') return;

      const card =

        node.querySelector('.order-design-card-bounds') ||

        node.querySelector('.order-design-minimal');

      const measureEl = card || node;

      const r = measureEl.getBoundingClientRect();

      if (r.width <= 0 || r.height <= 0) return;

      const area = r.width * r.height;

      if (area > bestArea) {

        bestArea = area;

        bestCenter = { x: r.left + r.width / 2, y: r.top + r.height / 2 };

      }

    });

    if (bestCenter) return bestCenter;

  }



  const slotZone = clientUnitSlotZoneFor(entry, userId);

  if (slotZone != null && entry.targetSlotIndex != null) {

    return getBattlebotSlotCenterPixels(slotZone, entry.targetSlotIndex);

  }

  return null;

}



function getDroneStartCenterPixels(entry, userId) {

  const droneEl = findDroneSlotElement(entry, userId);

  return getVisibleElementCenter(droneEl);

}



function detectReducedMotion() {

  if (typeof window === 'undefined' || !window.matchMedia) return false;

  try {

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  } catch (_) {

    return false;

  }

}



function isMission3OrbitalDropReveal(entry, gameState) {

  return (

    gameState?.missionId === 'chapter_1_mission_3' &&

    gameState?.campaignMode === true &&

    entry?.targetUnitInstanceId != null

  );

}



export default function DroneOrbitalDropWeaponOverlay({

  gameState,

  userId,

  weaponReveal,

}) {

  const seenKeysRef = useRef(new Set());

  const cleanupTimersRef = useRef(new Set());

  const nextAnimIdRef = useRef(1);

  const introReplayedRef = useRef(false);

  const introRetryTimerRef = useRef(null);

  const gameIdRef = useRef(null);



  const [animationItems, setAnimationItems] = useState([]);

  const [reducedMotion, setReducedMotion] = useState(detectReducedMotion);



  useEffect(() => {

    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    let mq;

    try {

      mq = window.matchMedia('(prefers-reduced-motion: reduce)');

    } catch (_) {

      return undefined;

    }

    const handler = (e) => setReducedMotion(!!e.matches);

    if (typeof mq.addEventListener === 'function') {

      mq.addEventListener('change', handler);

      return () => mq.removeEventListener('change', handler);

    }

    if (typeof mq.addListener === 'function') {

      mq.addListener(handler);

      return () => mq.removeListener(handler);

    }

    return undefined;

  }, []);



  useEffect(() => {

    return () => {

      for (const timerId of cleanupTimersRef.current) {

        clearTimeout(timerId);

      }

      cleanupTimersRef.current.clear();

      if (introRetryTimerRef.current) {

        clearTimeout(introRetryTimerRef.current);

        introRetryTimerRef.current = null;

      }

    };

  }, []);



  const scheduleAnimationCleanup = useCallback(

    (animId, targetUnitInstanceId) => {

      const timerId = setTimeout(() => {

        setAnimationItems((prev) => prev.filter((item) => item.id !== animId));

        cleanupTimersRef.current.delete(timerId);

        if (weaponReveal?.clearPending && targetUnitInstanceId) {

          weaponReveal.clearPending(targetUnitInstanceId);

        }

      }, TOTAL_ANIMATION_MS);

      cleanupTimersRef.current.add(timerId);

    },

    [weaponReveal]

  );



  const spawnAnimationFromEntry = useCallback(

    (entry, { playSound = false } = {}) => {

      const boxStart = getDroneStartCenterPixels(entry, userId);

      const unitCenter = getUnitTargetCenterPixels(entry, userId);

      if (!boxStart || !unitCenter) return false;



      if (

        weaponReveal?.markPending &&

        isMission3OrbitalDropReveal(entry, gameState)

      ) {

        weaponReveal.markPending(entry.targetUnitInstanceId);

      }



      const animId = nextAnimIdRef.current;

      nextAnimIdRef.current += 1;



      setAnimationItems((prev) => [

        ...prev,

        {

          id: animId,

          boxStartX: boxStart.x,

          boxStartY: boxStart.y,

          slotX: unitCenter.x,

          slotY: unitCenter.y,

        },

      ]);

      scheduleAnimationCleanup(animId, entry.targetUnitInstanceId);



      if (playSound) {

        try {

          playSoundEffect('/sounds/equip.mp3', 0.5);

        } catch (_) {

          /* sound optional */

        }

      }



      return true;

    },

    [userId, gameState, weaponReveal, scheduleAnimationCleanup]

  );



  const processEntry = useCallback(

    (entry, options) => {

      const key = actionLogEntryKey(entry);

      if (seenKeysRef.current.has(key)) return false;

      const spawned = spawnAnimationFromEntry(entry, options);

      if (spawned) {

        seenKeysRef.current.add(key);

      }

      return spawned;

    },

    [spawnAnimationFromEntry]

  );



  useEffect(() => {

    const gid = gameState?.gameId ?? null;

    if (gid != null && gameIdRef.current !== gid) {

      gameIdRef.current = gid;

      introReplayedRef.current = false;

      seenKeysRef.current = new Set();

    }

  }, [gameState?.gameId]);



  useEffect(() => {

    const log = Array.isArray(gameState?.actionLog) ? gameState.actionLog : [];

    const equipEntries = log.filter(

      (e) => e && e.type === 'drone-orbital-drop-weapon-equipped'

    );

    if (equipEntries.length === 0) return;



    let playedSound = false;

    for (const entry of equipEntries) {

      const isOpponent =

        entry.ownerPlayerId != null && String(entry.ownerPlayerId) !== String(userId);

      const spawned = processEntry(entry, { playSound: isOpponent && !playedSound });

      if (spawned && isOpponent) playedSound = true;

    }



    if (seenKeysRef.current.size > SEEN_KEY_HARD_CAP) {

      const recent = equipEntries.slice(-SEEN_KEY_SOFT_CAP).map(actionLogEntryKey);

      seenKeysRef.current = new Set(recent);

    }

  }, [gameState, userId, processEntry]);



  useEffect(() => {

    if (!shouldScheduleMission3OrbitalDropIntro(gameState)) return undefined;

    if (introReplayedRef.current) return undefined;

    if (userId == null) return undefined;



    const tryIntroReplay = (attempt = 0) => {

      if (introReplayedRef.current) return;

      const introEntry = buildMission3IntroOrbitalDropEntry(gameState, userId);

      if (!introEntry) {
        if (attempt < 20) {
          introRetryTimerRef.current = setTimeout(() => tryIntroReplay(attempt + 1), 200);
        }
        return;
      }



      const key = actionLogEntryKey(introEntry);

      if (seenKeysRef.current.has(key)) {

        introReplayedRef.current = true;

        return;

      }



      const spawned = spawnAnimationFromEntry(introEntry, { playSound: true });

      if (spawned) {

        seenKeysRef.current.add(key);

        introReplayedRef.current = true;

        return;

      }



      if (attempt < 25) {

        introRetryTimerRef.current = setTimeout(() => tryIntroReplay(attempt + 1), 200);

      } else if (weaponReveal?.clearPending) {

        weaponReveal.clearPending(introEntry.targetUnitInstanceId);

      }

    };



    introRetryTimerRef.current = setTimeout(() => tryIntroReplay(0), 350);



    return () => {

      if (introRetryTimerRef.current) {

        clearTimeout(introRetryTimerRef.current);

        introRetryTimerRef.current = null;

      }

    };

  }, [gameState, userId, spawnAnimationFromEntry, weaponReveal]);



  if (animationItems.length === 0) return null;

  if (typeof document === 'undefined' || !document.body) return null;



  return createPortal(

    <div

      className="drone-orbital-drop-weapon-overlay"

      style={{

        position: 'fixed',

        inset: 0,

        pointerEvents: 'none',

        zIndex: 15070,

      }}

      aria-hidden="true"

    >

      {animationItems.map((item) => {

        const dx = item.slotX - item.boxStartX;

        const dy = item.slotY - item.boxStartY;

        const animationName = reducedMotion

          ? 'drone-orbital-drop-weapon-box-flash'

          : 'drone-orbital-drop-weapon-box-arc';

        return (

          <img

            key={item.id}

            src={WEAPONS_BOX_IMAGE_SRC}

            alt=""

            aria-hidden="true"

            className="drone-orbital-drop-weapon-box"

            draggable={false}

            style={{

              position: 'fixed',

              left: `${item.boxStartX}px`,

              top: `${item.boxStartY}px`,

              width: '64px',

              height: '64px',

              transform: 'translate(-50%, -50%)',

              animation: `${animationName} ${BOX_TRAVEL_DURATION_MS + BOX_FADE_DURATION_MS}ms cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards`,

              ['--drone79-box-dx']: `${dx}px`,

              ['--drone79-box-dy']: `${dy}px`,

            }}

          />

        );

      })}

    </div>,

    document.body

  );

}


